(() => {
  'use strict';

  if (window.CzSwatchCartQueue) return;

  const normalizeIds = ids => new Set(Array.from(ids || [], id => String(id)));
  const sameSet = (left, right) => left.size === right.size && Array.from(left).every(id => right.has(id));

  function create(options) {
    const limit = Number(options.limit) || 20;
    const delay = Number(options.delay) || 60;
    const extraIds = normalizeIds(options.extraIds);
    let regularIds = normalizeIds(options.regularIds);
    let regularIdsKnown = Array.isArray(options.regularIds) || options.regularIds instanceof Set;
    let authoritative = normalizeIds(options.initialIds);
    let desired = new Set(authoritative);
    let timer = null;
    let active = null;
    let lastError = null;
    let intentVersion = 0;

    const hasRegular = ids => Array.from(ids).some(id => {
      if (extraIds.has(id)) return false;
      if (!regularIdsKnown) return true;
      // A newly selected card has not reached the guard's server snapshot yet.
      // Treat it as a possible regular swatch so it can be saved and classified;
      // already-authoritative items must be explicitly confirmed as regular.
      return regularIds.has(id) || !authoritative.has(id);
    });
    const snapshot = () => ({
      authoritative: new Set(authoritative),
      desired: new Set(desired),
      busy: Boolean(active || timer),
      error: lastError
    });

    function paint() {
      options.onOptimistic?.(snapshot());
    }

    function status(state, message) {
      options.onStatus?.(state, message || '');
    }

    function schedule() {
      if (active || timer || sameSet(authoritative, desired)) return;
      status('saving', 'Saving…');
      timer = setTimeout(() => {
        timer = null;
        drain().catch(() => {});
      }, delay);
    }

    function set(id, selected) {
      id = String(id);
      if (selected && !desired.has(id) && desired.size >= limit) {
        options.onLimit?.(limit);
        paint();
        return false;
      }

      if (selected) desired.add(id);
      else desired.delete(id);

      // Extra Swatches can never remain without at least one ordinary swatch.
      if (!hasRegular(desired)) extraIds.forEach(extraId => desired.delete(extraId));

      intentVersion += 1;
      lastError = null;
      paint();
      schedule();
      return true;
    }

    function toggle(id) {
      id = String(id);
      return set(id, !desired.has(id));
    }

    function buildBatchTarget() {
      const target = new Set(desired);
      // The existing Extra guard verifies the server before allowing Extra. If a
      // regular swatch and Extra were selected together, confirm regular first.
      if (!hasRegular(authoritative) && hasRegular(target)) {
        extraIds.forEach(id => {
          if (!authoritative.has(id)) target.delete(id);
        });
      }
      return target;
    }

    function updatesFor(target) {
      const updates = {};
      new Set([...authoritative, ...target]).forEach(id => {
        const current = authoritative.has(id) ? 1 : 0;
        const next = target.has(id) ? 1 : 0;
        if (current !== next) updates[id] = next;
      });
      return updates;
    }

    async function run() {
      status('saving', 'Saving…');
      while (!sameSet(authoritative, desired)) {
        const batchVersion = intentVersion;
        const target = buildBatchTarget();
        const updates = updatesFor(target);
        if (!Object.keys(updates).length) {
          // Only a deferred Extra addition remains; the next loop follows the
          // now-authoritative regular swatch.
          authoritative = new Set(target);
          continue;
        }

        try {
          const serverIds = await options.write(updates, new Set(target));
          authoritative = normalizeIds(serverIds == null ? target : serverIds);
          lastError = null;
          options.onAuthoritative?.(snapshot());
          paint();
        } catch (error) {
          lastError = error instanceof Error ? error : new Error(String(error || 'Cart save failed'));
          try {
            const serverIds = await options.recover?.();
            if (serverIds) authoritative = normalizeIds(serverIds);
          } catch (_) {}
          paint();
          status('error', 'Couldn’t save your swatches. Retry before checkout.');
          throw lastError;
        }

        // A response may describe an older target while newer clicks remain in
        // desired. The loop writes that newer intent next; it never replaces it.
        if (batchVersion !== intentVersion) status('saving', 'Saving…');
      }
      status('saved', 'Saved');
      return snapshot();
    }

    function drain() {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      if (active) return active;
      if (sameSet(authoritative, desired)) {
        if (!lastError) status('saved', 'Saved');
        return Promise.resolve(snapshot());
      }
      active = run().finally(() => {
        active = null;
        if (!sameSet(authoritative, desired) && !lastError) schedule();
      });
      return active;
    }

    function replaceAuthoritative(ids) {
      authoritative = normalizeIds(ids);
      if (!active && !timer && !lastError) desired = new Set(authoritative);
      paint();
      return snapshot();
    }

    function confirmAuthoritative(ids) {
      authoritative = normalizeIds(ids);
      paint();
      return snapshot();
    }

    function setRegularIds(ids) {
      regularIds = normalizeIds(ids);
      regularIdsKnown = true;
      if (!hasRegular(desired)) extraIds.forEach(id => desired.delete(id));
      paint();
      schedule();
      return snapshot();
    }

    paint();
    return {
      toggle,
      set,
      flush: drain,
      retry() { lastError = null; return drain(); },
      replaceAuthoritative,
      confirmAuthoritative,
      setRegularIds,
      snapshot
    };
  }

  window.CzSwatchCartQueue = { create };
})();
